import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Hub } from 'aws-amplify';

import {
  Route,
  Redirect,
  Switch,
  useLocation,
  RouteProps,
  RouteComponentProps
} from 'react-router-dom';
import {
  Auth,
  MultiOrgSignInPanel,
  UserTermsPanel,
  Login,
  OneTimePasscode,
  TemporaryCode,
  LodgeNewEnquiry,
  Dashboard,
  Members,
  Registration,
  AreaOfInterest,
  AreaOfInterestsViewer,
  AssetOwnerEmails,
  ReferralChannels,
  ReferralHistory,
  OrganisationProfile,
  EnquiryDetails,
  OrganisationUsers,
  EnquiryHistory,
  EnquiryPresets,
  UserApiKeys,
  WebHookSubscriptions,
  WebHookHistory,
  GISIntegration,
  Mobilization,
  SearchEmails,
  SearchUsers,
  Reports,
  ReportsCrs,
  EmergencyContacts,
  EmailsUndelivered,
  ManageAnnouncements,
  PremiumFeatures,
  PremiumSelfService,
  BulkEnquiryLodgement,
  BydaMemberLookup,
  WebLauncherLanding,
  Response,
  DocumentPreview,
  EnquiryLodged,
  ISeekPlant,
  FireAnts,
  ExternalApp,
  PurchaseTransactions,
  CertLocLocator
} from 'modules';
import AuthRoute from './AuthRoute';
import {
  ALL_USERROLES,
  END_USER_PREMIUM_USERROLES,
  APP_URL,
  SUPPORT_SYS_ADMIN_USERROLES,
  SUPPORT_SYS_DBYD_AO_ADMIN_USERROLES,
  SUPPORT_SYS_DBYD_AO_ADMIN_AO_VIEWER_USERROLES,
  USERSROLE,
  SCREEN_SIZE
} from 'types';
import { selectTheme } from 'redux/app/selectors';
import {
  selectIsAuthenticated,
  selectSessionExipyIn,
  selectScreenSize,
  currentUser,
  selectRedirectPath
} from 'redux/session/selectors';
import {
  hasResidentialUserType,
  isResidentialUser,
  redirectResidentialShareEnquiry,
  toPendingPath
} from 'utilities/residential-redirect.util';
import {
  setScreenSize,
  setRedirectPath,
  setIsFromSwxLocate
} from 'redux/session/actions';
import {
  fetchAppFailure,
  initAppLoading,
  resetAppAuthState
} from 'redux/app/actions';
import { getAnnouncementsStart } from 'redux/announcement';
import { Message } from 'modules/generic/message';
import { useEventListener, useComponentMount } from 'utilities/hooks.util';
import { MAX, XL, LG, MD } from 'appConstants/layout';
import { setAppVisiblePanel } from 'redux/app/actions';
import { setShowInterstitialPage } from 'redux/app/actions';
import SafetyMessageAndLogo from 'modules/safetyMessageAndLogo';
import { debounce } from 'lodash';

export const APP = 'app';

const EnquiryDetailsRoute: React.FC<RouteComponentProps<{ id?: string }>> = (
  props
) => {
  const user = useSelector(currentUser);
  const { pathname, search } = props.location;
  const shareId = new URLSearchParams(search).get('shareId');
  const shouldRedirectToResidential = !!shareId && isResidentialUser(user);

  useEffect(() => {
    redirectResidentialShareEnquiry(toPendingPath({ pathname, search }), user);
  }, [pathname, search, user]);

  if (shouldRedirectToResidential) {
    return null;
  }

  return <EnquiryDetails {...props} />;
};

const AppRoutes: React.FC<RouteProps> = () => {
  const dispatch: any = useDispatch();
  const location = useLocation();
  const appTheme = useSelector(selectTheme);
  const sessionExpiryIn = useSelector(selectSessionExipyIn);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const screenSize = useSelector(selectScreenSize);
  const redirectPath = useSelector(selectRedirectPath);
  const sessionUser = useSelector(currentUser);
  const hidePremiumFeatures = hasResidentialUserType(sessionUser);

  const { gtag } = window;
  const [user, setUser] = useState<any>();
  const AllEnquiryHistory = useMemo(
    () => () => <EnquiryHistory isFullSet={true} />,
    []
  );

  const handleResize = useCallback(() => {
    const height = document.documentElement?.clientHeight || window.innerHeight;
    const width = window.innerWidth;
    document.documentElement.style.setProperty(
      '--app-height',
      `${height - 1}px`
    );
    document.documentElement.style.setProperty('overflow-x', `hidden`);
    if (width >= MAX) {
      dispatch(setScreenSize(SCREEN_SIZE.MAX));
    } else if (width < MAX && width >= XL) {
      dispatch(setScreenSize(SCREEN_SIZE.XL));
    } else if (width < XL && width > LG) {
      dispatch(setScreenSize(SCREEN_SIZE.LG));
    } else if (width <= LG && width >= MD) {
      dispatch(setScreenSize(SCREEN_SIZE.MD));
    } else {
      dispatch(setScreenSize(SCREEN_SIZE.SM));
    }
  }, [dispatch]);

  // Create debounced version to reliably handle orientation changes on iPad
  // while filtering out rapid resize events during iOS zoom operations
  const debouncedHandleResize = useMemo(() => debounce(handleResize, 200), [
    handleResize
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const origin = params.get('origin');
    Hub.listen('auth', ({ payload: { event, data } }) => {
      if (data?.message) {
        dispatch(fetchAppFailure(data?.message));
      }
      switch (event) {
        case 'signIn':
        case 'cognitoHostedUI':
          setUser(data);
          break;
        case 'signOut':
        case 'oAuthSignOut':
        case 'signIn_failure':
        case 'cognitoHostedUI_failure':
        case 'tokenRefresh_failure':
          window.location.pathname !== APP_URL.TEMPORARY_CODE && setUser(null);
          break;
        case 'customOAuthState':
          dispatch(setRedirectPath(data));
          break;
        default:
          break;
      }
    });
    origin !== 'swxlocate' &&
      window.location.pathname !== APP_URL.EXTERNAL_APP &&
      window.location.pathname !== APP_URL.TEMPORARY_CODE &&
      !window.location.pathname.includes('/iseekplant/') &&
      dispatch(getAnnouncementsStart({ anonymous: !isAuthenticated }));
    dispatch(setShowInterstitialPage(true));
  }, [dispatch, isAuthenticated, sessionExpiryIn]);

  useEffect(() => {
    user && dispatch(initAppLoading());
    user === null && dispatch(resetAppAuthState());
  }, [dispatch, user]);

  useEffect(() => {
    if (typeof gtag === 'function') {
      gtag('event', 'page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname,
        send_to: process.env.REACT_APP_GA_TRACKING_CODE
      });
    }
  }, [dispatch, location, gtag]);

  useEffect(() => {
    if (location.pathname === redirectPath) {
      dispatch(setRedirectPath(undefined));
    }
  }, [dispatch, location, redirectPath]);

  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const visiblePanel = params.get('visible-panel');
      const origin = params.get('origin');
      visiblePanel && dispatch(setAppVisiblePanel(visiblePanel));

      const isIframe = window.self !== window.top && origin === 'swxlocate';
      dispatch(setIsFromSwxLocate(isIframe));
      // Force ArcGIS CSS loading in iframe context
      if (isIframe) {
        // Wait a bit for the page to load, then check CSS
        setTimeout(() => {
          const arcgisCSS = document.querySelector('link[href*="arcgis"]');
          if (!arcgisCSS) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://js.arcgis.com/4.33/esri/themes/light/main.css';
            document.head.appendChild(link);
          }
        }, 1000);
      }
    } else {
      dispatch(setIsFromSwxLocate(false));
    }
  }, [dispatch, location]);

  useEffect(() => {
    if (user && typeof gtag === 'function') {
      // send user id to GA
      gtag('set', {
        user_id: user.id
      });
    }
  }, [user, gtag]);

  // Use debounced resize listener for all devices to:
  // - Reliably detect orientation changes on iPad (screen.orientation doesn't fire reliably)
  // - Filter out rapid resize events during iOS zoom operations
  useEventListener('resize', debouncedHandleResize, window);

  useComponentMount(() => {
    handleResize();
  });

  /**
   * Manage reCAPTCHA script lifecycle for lodge enquiry route.
   *
   * WHY: Lodge enquiry uses invisible reCAPTCHA (score-based) and needs the
   * `grecaptcha.enterprise` global available at submit time. We load the script
   * only on the lodge enquiry route and remove it (and the badge) on route exit
   * to avoid leaving global artifacts around the rest of the app.
   *
   * HOW: On entering the lodge enquiry route, inject
   * `enterprise.js?render=<siteKey>` with a stable id (`g-recaptcha-v3-key`).
   * On cleanup, remove that script and the first `.grecaptcha-badge` element.
   */
  useEffect(() => {
    const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_V3_KEY || '';

    const recaptchaRoutes: string[] = [
      APP_URL.LODGE_NEW_ENQUIRY,
      APP_URL.PURCHASE_TRANSACTIONS,
      APP_URL.BYDA_MEMBER_LOOKUP,
      APP_URL.BULK_ENQUIRY_LODGEMENT
    ];
    if (recaptchaRoutes.includes(location.pathname)) {
      if (siteKey && !document.getElementById('g-recaptcha-v3-key')) {
        const script = document.createElement('script');
        script.id = 'g-recaptcha-v3-key';
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
      }
    }

    return () => {
      document.getElementById('g-recaptcha-v3-key')?.remove();
      document.querySelector('.grecaptcha-badge')?.remove();
    };
  }, [location.pathname]);

  return (
    <div
      className={`app --swx-theme--${appTheme}${
        sessionStorage.getItem('user') ? ' iUActive' : ''
      }${screenSize === SCREEN_SIZE.SM ? ' --swx-typography--sm' : ''}`}
      data-testid="app"
      id="app"
    >
      <Switch>
        <Route
          key="Auth"
          exact={true}
          path={APP_URL.AUTH}
          render={(props: any) => <Auth {...props} />}
        />
        <Route
          key="Login"
          exact={true}
          path={APP_URL.LOGIN}
          render={(props: any) => <Login {...props} />}
        />
        <Route
          key="TemporaryCode"
          path={APP_URL.TEMPORARY_CODE}
          render={(props: any) => <TemporaryCode {...props} />}
        />
        <Route
          key="OneTimePasscode"
          path={APP_URL.ONE_TIME_PASSCODE}
          render={(props: any) => <OneTimePasscode {...props} />}
        />
        <Route
          key="Registration"
          path={APP_URL.REGISTRATION}
          render={(props: any) => <Registration {...props} />}
        />
        <Route
          key="UserTerms"
          path={APP_URL.AUTH_USER_TERMS}
          exact={true}
          render={(props: any) => <UserTermsPanel {...props} />}
        />
        <Route
          exact={true}
          key="AuthMultiOrganisation"
          path={APP_URL.AUTH_MULTI_ORGANISATION}
          render={(props: any) => <MultiOrgSignInPanel {...props} />}
        />
        <Route
          key="emergency"
          path={APP_URL.EMERGENCY}
          render={(props: any) => <EmergencyContacts {...props} />}
        />
        <Route
          key="mobilization"
          exact={true}
          path={APP_URL.MOBILISATION}
          render={(props: any) => <Mobilization {...props} />}
        />
        <Route
          key="messages"
          exact={true}
          path={APP_URL.MESSAGES}
          render={(props: any) => <Message {...props} />}
        />
        <Route
          key="Response"
          exact={true}
          path={APP_URL.RESPONSE}
          render={(props: any) => <Response {...props} />}
        />
        <Route
          key="ExternalApp"
          path={APP_URL.EXTERNAL_APP}
          render={(props: any) => <ExternalApp {...props} />}
        />
        <Route
          key="ISeekPlant"
          path={APP_URL.ISEEKPLANT}
          render={(props: any) => <ISeekPlant {...props} />}
        />
        <Route
          key="FireAnts"
          path={APP_URL.FIREANTS}
          render={(props: any) => <FireAnts {...props} />}
        />
        <Route
          key="CertLocLocator"
          path={APP_URL.CERTLOC_LOCATOR}
          render={(props: any) => <CertLocLocator {...props} />}
        />
        {/* ALL_USERROLES */}
        <AuthRoute
          key="LodgeNewEnquiry"
          path={APP_URL.LODGE_NEW_ENQUIRY}
          exact={true}
          page={LodgeNewEnquiry}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="CloneExistingEnquiry"
          path={APP_URL.CLONE_EXISTING_ENQUIRY}
          exact={true}
          page={LodgeNewEnquiry}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="AllEnquiryHistory"
          path={APP_URL.ALL_ENQUIRIY_HISTORY}
          exact={true}
          page={AllEnquiryHistory}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="AllEnquiryHistoryDetails"
          path={APP_URL.ALL_ENQUIRY_HISTORY_DETAILS}
          exact={true}
          page={AllEnquiryHistory}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="EnquiryPresets"
          path={APP_URL.ENQUIRY_PRESETS}
          exact={true}
          page={EnquiryPresets}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="EnquiryHistory"
          path={APP_URL.ENQUIRIY_HISTORY}
          exact={true}
          page={EnquiryHistory}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="EnquiryLodged"
          path={APP_URL.ENQUIRY_LODGED}
          exact={true}
          page={EnquiryLodged}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="EnquiryDetails"
          path={APP_URL.ENQUIRY_DETAILS}
          exact={true}
          page={EnquiryDetailsRoute}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="Dashboard"
          path={APP_URL.DASHBOARD}
          exact={true}
          page={Dashboard}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="assetOwnerEmails"
          path={APP_URL.ASSET_OWNER_EMAILS}
          exact={true}
          page={AssetOwnerEmails}
          requiredRole={[USERSROLE.AOADMIN, USERSROLE.AOVIEWER]}
        />
        <AuthRoute
          key="AreaOfInterestsViewer"
          path={APP_URL.AOI_VIEWER}
          exact={true}
          page={AreaOfInterestsViewer}
          requiredRole={[
            USERSROLE.DBYDADMIN,
            USERSROLE.SUPPORT,
            USERSROLE.SYSADMIN
          ]}
        />
        <AuthRoute
          key="Members"
          path={APP_URL.MEMBERS}
          exact={true}
          page={Members}
          requiredRole={[
            USERSROLE.DBYDADMIN,
            USERSROLE.SUPPORT,
            USERSROLE.SYSADMIN
          ]}
        />
        <AuthRoute
          key="AreaOfInterest"
          path={APP_URL.MANAGE_AOI}
          exact={true}
          page={AreaOfInterest}
          requiredRole={[USERSROLE.AOADMIN]}
        />
        <AuthRoute
          key="ReferralChannels"
          path={APP_URL.MANAGE_REFERRAL_CHANNEL}
          exact={true}
          page={ReferralChannels}
          requiredRole={[USERSROLE.AOADMIN]}
        />
        <AuthRoute
          key="OrganisationProfile"
          path={APP_URL.ORGANISATION_PROFILE}
          exact={true}
          page={OrganisationProfile}
          requiredRole={[USERSROLE.AOADMIN]}
        />
        <AuthRoute
          key="SafetyMessageAndLogo"
          path={APP_URL.MANAGE_SAFETY_MESSAGE_AND_LOGO}
          exact={true}
          page={SafetyMessageAndLogo}
          requiredRole={[USERSROLE.AOADMIN]}
        />
        <AuthRoute
          key="OrganisationUsers"
          path={APP_URL.USERS_ADMIN}
          exact={true}
          page={OrganisationUsers}
          requiredRole={SUPPORT_SYS_DBYD_AO_ADMIN_USERROLES}
        />

        <AuthRoute
          key="Apikeys"
          path={APP_URL.USERS_API_KEYS}
          exact={true}
          page={UserApiKeys}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="WebHookSubscriptions"
          path={APP_URL.WEB_HOOKS_SUBSCRIPTIONS}
          exact={true}
          page={WebHookSubscriptions}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="WebHookHistory"
          path={APP_URL.WEB_HOOKS_HISTORY}
          exact={true}
          page={WebHookHistory}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="GISIntegration"
          path={APP_URL.GIS_INTEGRATIONS}
          exact={true}
          page={GISIntegration}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="SearchEmails"
          path={APP_URL.SEARCH_EMAILS}
          exact={true}
          page={SearchEmails}
          requiredRole={SUPPORT_SYS_ADMIN_USERROLES}
        />
        <AuthRoute
          key="SearchUsers"
          path={APP_URL.SEARCH_SUPPORT_USERS}
          exact={true}
          page={SearchUsers}
          requiredRole={SUPPORT_SYS_ADMIN_USERROLES}
        />
        <AuthRoute
          key="ReportsCrs"
          path={APP_URL.REPORTS}
          exact={true}
          page={ReportsCrs}
          requiredRole={SUPPORT_SYS_DBYD_AO_ADMIN_AO_VIEWER_USERROLES}
        />
        <AuthRoute
          key="ReportsOld"
          path={APP_URL.REPORTS_OLD}
          exact={true}
          page={Reports}
          requiredRole={SUPPORT_SYS_DBYD_AO_ADMIN_AO_VIEWER_USERROLES}
        />
        <AuthRoute
          key="ReferralHistory"
          path={APP_URL.REFERRAL_HISTORY}
          exact={true}
          page={ReferralHistory}
          requiredRole={[USERSROLE.AOADMIN, USERSROLE.AOVIEWER]}
        />
        <AuthRoute
          key="EmailsUndelivered"
          path={APP_URL.UNDELIVERED_EMAIL}
          exact={true}
          page={EmailsUndelivered}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="ManageAnnouncements"
          path={APP_URL.MANAGE_ANNOUNCEMENTS}
          exact={true}
          page={ManageAnnouncements}
          requiredRole={[USERSROLE.SYSADMIN, USERSROLE.DBYDADMIN]}
        />
        <AuthRoute
          key="PremiumFeatures"
          path={APP_URL.PREMIUM_FEATURES}
          exact={true}
          page={PremiumFeatures}
          requiredRole={[USERSROLE.SYSADMIN, USERSROLE.DBYDADMIN]}
        />
        {process.env.REACT_APP_PREMIUM_SELF_SERVICE_ENABLED === 'true' &&
          !hidePremiumFeatures && (
            <AuthRoute
              key="PremiumSelfService"
              path={APP_URL.PREMIUM_SELF_SERVICE}
              exact={true}
              page={PremiumSelfService}
              requiredRole={END_USER_PREMIUM_USERROLES}
            />
          )}
        {process.env.REACT_APP_PREMIUM_SELF_SERVICE_ENABLED === 'true' &&
          !hidePremiumFeatures && (
            <AuthRoute
              key="BulkEnquiryLodgement"
              path={APP_URL.BULK_ENQUIRY_LODGEMENT}
              exact={true}
              page={BulkEnquiryLodgement}
              requiredRole={END_USER_PREMIUM_USERROLES}
            />
          )}
        {process.env.REACT_APP_PREMIUM_SELF_SERVICE_ENABLED === 'true' &&
          !hidePremiumFeatures && (
            <AuthRoute
              key="BydaMemberLookup"
              path={APP_URL.BYDA_MEMBER_LOOKUP}
              exact={true}
              page={BydaMemberLookup}
              requiredRole={END_USER_PREMIUM_USERROLES}
            />
          )}
        {process.env.REACT_APP_PREMIUM_SELF_SERVICE_ENABLED === 'true' &&
          !hidePremiumFeatures && (
            <AuthRoute
              key="PurchaseTransactions"
              path={APP_URL.PURCHASE_TRANSACTIONS}
              exact={true}
              page={PurchaseTransactions}
              requiredRole={END_USER_PREMIUM_USERROLES}
            />
          )}
        <AuthRoute
          key="WebLauncherLanding"
          path={APP_URL.WEB_LAUNCHER_LANDING}
          exact={true}
          page={WebLauncherLanding}
          requiredRole={ALL_USERROLES}
        />
        <AuthRoute
          key="DocumentPreview"
          path={APP_URL.DOCUMENT_PREVIEW}
          exact={true}
          page={DocumentPreview}
          requiredRole={ALL_USERROLES}
        />
        <Route path="*">
          <Redirect to={APP_URL.AUTH} />
        </Route>
      </Switch>
    </div>
  );
};

AppRoutes.displayName = 'RootApp';
export default AppRoutes;
